import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const CapsAlignment: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>CAPS Alignment — Sprout Docs</title>
        <meta
          name="description"
          content="How Sprout aligns observations and assessments to the CAPS curriculum: pre-loaded Foundation Phase outcomes, custom outcomes, and roadmap for Intermediate Phase."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/docs/curriculum/caps-alignment" />
      </Helmet>

      <h1>CAPS Alignment</h1>
      <p className="lead">
        Sprout is structured around the Curriculum and Assessment Policy
        Statement (CAPS). Every observation and assessment can be linked to
        a specific learning outcome, which is what makes our reports useful
        rather than just a list of dated notes.
      </p>

      <h2>What ships pre-loaded</h2>
      <p>
        New organisations come with the CAPS Foundation Phase data already
        seeded — Grades R through 3 — across the four learning areas:
      </p>
      <ul>
        <li>Home Language</li>
        <li>First Additional Language</li>
        <li>Mathematics</li>
        <li>Life Skills</li>
      </ul>
      <p>
        That covers roughly one hundred and fifty-five learning outcomes,
        sourced from the official Department of Basic Education CAPS
        documents. Teachers don't need to set any of this up themselves.
      </p>

      <h2>How it shows up in the product</h2>
      <p>
        When a teacher creates an observation, they pick a learning area and
        then a specific outcome. Assessments work the same way, with the
        addition of a configurable scoring scale. Linking each entry to an
        outcome is what enables:
      </p>
      <ul>
        <li>Per-child progress reports grouped by learning area.</li>
        <li>Classroom-level views showing which outcomes have coverage and which haven't.</li>
        <li>Filtering observations by outcome when reviewing a child's term.</li>
      </ul>

      <h2>Custom outcomes</h2>
      <p>
        Some schools follow CAPS but layer their own programme on top — a
        Montessori scope and sequence, a Reggio-inspired framework, or a
        bespoke values curriculum. Organisation admins can add custom
        outcomes alongside the built-in CAPS ones (on any plan), and teachers
        can tag observations against either set.
      </p>

      <h2>Source and updates</h2>
      <p>
        The pre-loaded outcome list is taken directly from official CAPS
        documents published by the DBE. When the DBE publishes amendments,
        we update the seeded data and existing organisations receive the
        new outcomes on their next deploy. Outcome IDs are stable so
        historical observations stay correctly linked.
      </p>

      <h2>Intermediate Phase and FET</h2>
      <p>
        We're focused on Foundation Phase first because that's where
        observation-driven assessment matters most and where we've had the
        most demand. Intermediate Phase (Grades 4-6) and FET coverage are
        on the roadmap; if your school needs them, please{' '}
        <Link to="/contact">let us know</Link> so we can prioritise based
        on real demand.
      </p>

      <p>
        See <Link to="/docs/getting-started/first-observation">Your First Observation</Link>{' '}
        for the day-to-day teacher flow.
      </p>
    </>
  );
};

export default CapsAlignment;
